import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Mountain, ArrowLeft, Clock, MapPin, TrendingUp, TrendingDown,
  Route, AlertTriangle, Ticket, Navigation, Loader2, Share2,
  Backpack, Calendar, Lightbulb, ExternalLink, ChevronDown, ChevronUp,
  Map
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import TrailMap from '@/components/TrailMap';
import ElevationProfile from '@/components/ElevationProfile';
import TrailSignage from '@/components/TrailSignage';
import SEO, { trailSchema } from '@/components/SEO';
import { ShareButton } from '@/components/ShareButton';

const TrailDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllTips, setShowAllTips] = useState(false);

  const labels = {
    es: {
      back: 'Volver',
      distance: 'Distancia',
      duration: 'Duración aprox.',
      elevationGain: 'Desnivel +',
      elevationLoss: 'Desnivel -',
      minAltitude: 'Altitud mín.',
      maxAltitude: 'Altitud máx.',
      startPoint: 'Inicio',
      endPoint: 'Fin',
      municipalities: 'Municipios',
      naturalSpaces: 'Espacios naturales',
      modality: 'Modalidad',
      difficulty: 'Dificultad',
      requiresPermit: 'Requiere permiso',
      requestPermit: 'Solicitar permiso',
      tips: 'Consejos para la ruta',
      whatToBring: 'Qué llevar',
      bestSeason: 'Mejor época',
      share: 'Compartir',
      viewExternal: 'Ver en TenerifeON',
      notFound: 'Sendero no encontrado',
      hours: 'horas',
      km: 'km',
      m: 'm',
      loading: 'Cargando...',
      routeInfo: 'Información de la ruta',
      technicalData: 'Datos técnicos',
      recommendations: 'Recomendaciones'
    },
    en: {
      back: 'Back',
      distance: 'Distance',
      duration: 'Est. duration',
      elevationGain: 'Elevation +',
      elevationLoss: 'Elevation -',
      minAltitude: 'Min. altitude',
      maxAltitude: 'Max. altitude',
      startPoint: 'Start',
      endPoint: 'End',
      municipalities: 'Municipalities',
      naturalSpaces: 'Natural spaces',
      modality: 'Modality',
      difficulty: 'Difficulty',
      requiresPermit: 'Permit required',
      requestPermit: 'Request permit',
      tips: 'Trail tips',
      whatToBring: 'What to bring',
      bestSeason: 'Best season',
      share: 'Share',
      viewExternal: 'View on TenerifeON',
      notFound: 'Trail not found',
      hours: 'hours',
      km: 'km',
      m: 'm',
      loading: 'Loading...',
      routeInfo: 'Route information',
      technicalData: 'Technical data',
      recommendations: 'Recommendations'
    },
    de: {
      back: 'Zurück',
      distance: 'Entfernung',
      duration: 'Geschätzte Dauer',
      elevationGain: 'Höhenmeter +',
      elevationLoss: 'Höhenmeter -',
      minAltitude: 'Min. Höhe',
      maxAltitude: 'Max. Höhe',
      startPoint: 'Start',
      endPoint: 'Ziel',
      municipalities: 'Gemeinden',
      naturalSpaces: 'Naturräume',
      modality: 'Modalität',
      difficulty: 'Schwierigkeit',
      requiresPermit: 'Genehmigung erforderlich',
      requestPermit: 'Genehmigung beantragen',
      tips: 'Wandertipps',
      whatToBring: 'Was mitnehmen',
      bestSeason: 'Beste Jahreszeit',
      share: 'Teilen',
      viewExternal: 'Auf TenerifeON ansehen',
      notFound: 'Wanderweg nicht gefunden',
      hours: 'Stunden',
      km: 'km',
      m: 'm',
      loading: 'Laden...',
      routeInfo: 'Routeninformation',
      technicalData: 'Technische Daten',
      recommendations: 'Empfehlungen'
    }
  };

  const t = labels[language] || labels.es;

  const getDifficultyLabel = (difficulty) => {
    const map = { 'Fácil': { en: 'Easy', de: 'Leicht' }, 'Moderada': { en: 'Moderate', de: 'Mittel' }, 'Difícil': { en: 'Hard', de: 'Schwer' } };
    return (language !== 'es' && map[difficulty]?.[language]) || difficulty;
  };

  const getAlertField = (alert, field) => {
    if (language === 'en' && alert[`${field}_en`]) return alert[`${field}_en`];
    if (language === 'de' && alert[`${field}_de`]) return alert[`${field}_de`];
    return alert[field];
  };

  const loadTrail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTrail(id, language);
      setTrail(res.data);
    } catch (error) {
      console.error('Error loading trail:', error);
      toast.error('Error al cargar el sendero');
    } finally {
      setLoading(false);
    }
  }, [id, language]);

  useEffect(() => {
    loadTrail();
  }, [loadTrail]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Fácil': return 'bg-green-500';
      case 'Moderada': return 'bg-yellow-500';
      case 'Difícil': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} ${t.km}`;
    }
    return `${Math.round(meters)} ${t.m}`;
  };

  const handleShare = async () => {
    const shareData = {
      title: trail.name,
      text: `${trail.name} - ${formatDistance(trail.distance_meters)} - ${trail.difficulty}`,
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FDFBF7]">
        <Loader2 className="animate-spin text-[#38B2AC]" size={40} />
      </div>
    );
  }

  if (!trail) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#FDFBF7] px-4">
        <Mountain size={64} className="text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">{t.notFound}</p>
        <Button variant="link" onClick={() => navigate('/trails')} className="text-[#38B2AC] mt-2">
          {t.back}
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-[#FDFBF7]" data-testid="trail-detail-page">
      <SEO
        title={`${trail.name} - ${getDifficultyLabel(trail.difficulty)}`}
        description={`${trail.name}: ${(trail.distance_meters/1000).toFixed(1)}km, +${Math.round(trail.elevation_gain)}m. ${trail.municipalities?.join(', ')}. ${trail.tips?.[0] || ''}`}
        path={`/trails/${trail.id}`}
        lang={language}
        schema={trailSchema(trail, language)}
      />
      {/* Header */}
      <div className={`bg-gradient-to-br ${getDifficultyColor(trail.difficulty)} to-gray-800 text-white`}>
        <div className="max-w-4xl mx-auto px-4 pt-4 pb-6 safe-top">
          {/* Back button */}
          <button 
            onClick={() => navigate('/trails')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm"
          >
            <ArrowLeft size={18} />
            {t.back}
          </button>

          {/* Trail name and badge */}
          <div className="flex items-start justify-between gap-3">
            <div>
              {trail.matricula && (
                <span className="inline-block text-xs font-mono bg-white/20 px-2 py-1 rounded mb-2">
                  {trail.matricula}
                </span>
              )}
              <h1 className="text-xl font-bold leading-tight">{trail.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <ShareButton kind="t" itemId={trail.id} variant="ghost" className="text-white/80 hover:text-white" testId="share-trail-btn" />
              <Badge className={`${getDifficultyColor(trail.difficulty)} text-white border-0`}>
                {getDifficultyLabel(trail.difficulty)}
              </Badge>
            </div>
          </div>

          {/* Alerts linked to this trail */}
          {trail.has_alert && trail.alerts && trail.alerts.length > 0 && (
            <div className="mt-3 space-y-2">
              {trail.alerts.map((alert, i) => (
                <a
                  key={alert.id || i}
                  href={alert.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 bg-amber-500/30 rounded-lg hover:bg-amber-500/40 transition-colors"
                  data-testid={`trail-alert-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="flex-shrink-0 mt-0.5 text-amber-100" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{getAlertField(alert, 'title')}</p>
                      {alert.description && (
                        <p className="text-xs mt-1 opacity-80 line-clamp-2">{getAlertField(alert, 'description').slice(0, 200)}</p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
          {trail.has_alert && !trail.alerts && trail.alert_message && (
            <div className="mt-3 p-3 bg-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{getAlertField(trail, 'alert_message')}</span>
            </div>
          )}

          {/* Permit required */}
          {trail.requires_permit && (
            <div className="mt-3">
              <a
                href={trail.permit_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                <Ticket size={16} />
                {t.requestPermit}
              </a>
            </div>
          )}

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <Route size={20} className="mx-auto mb-1 opacity-80" />
              <div className="font-bold">{formatDistance(trail.distance_meters)}</div>
              <div className="text-xs opacity-70">{t.distance}</div>
            </div>
            {trail.duration_hours && (
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <Clock size={20} className="mx-auto mb-1 opacity-80" />
                <div className="font-bold">{trail.duration_hours} {t.hours}</div>
                <div className="text-xs opacity-70">{t.duration}</div>
              </div>
            )}
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <TrendingUp size={20} className="mx-auto mb-1 opacity-80" />
              <div className="font-bold">+{Math.round(trail.elevation_gain)}{t.m}</div>
              <div className="text-xs opacity-70">{t.elevationGain}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Trail Signage */}
        {trail.matricula && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <TrailSignage matricula={trail.matricula} language={language} />
          </div>
        )}

        {/* Map with Route */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Map size={18} className="text-[#38B2AC]" />
            {language === 'es' ? 'Mapa de la ruta' : language === 'de' ? 'Routenkarte' : 'Route Map'}
          </h2>
          <TrailMap
            trackCoordinates={trail.track_coordinates}
            startLat={trail.start_latitude}
            startLng={trail.start_longitude}
            endLat={trail.end_latitude}
            endLng={trail.end_longitude}
            name={trail.name}
          />
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> {language === 'es' ? 'Inicio' : language === 'de' ? 'Start' : 'Start'}
            </span>
            {trail.modality === 'Lineal' && (
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> {language === 'es' ? 'Final' : language === 'de' ? 'Ziel' : 'End'}
              </span>
            )}
          </div>
        </div>

        {/* Elevation Profile */}
        {(trail.min_altitude || trail.max_altitude) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingUp size={18} className="text-[#38B2AC]" />
              {language === 'es' ? 'Perfil de alturas' : language === 'de' ? 'Höhenprofil' : 'Elevation Profile'}
            </h2>
            <ElevationProfile
              minAlt={trail.min_altitude}
              maxAlt={trail.max_altitude}
              elevGain={trail.elevation_gain}
              elevLoss={trail.elevation_loss}
              distance={trail.distance_meters}
            />
          </div>
        )}

        {/* Route Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin size={18} className="text-[#38B2AC]" />
            {t.routeInfo}
          </h2>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <span className="text-gray-500">{t.startPoint}</span>
              <span className="font-medium text-gray-900 text-right">{trail.start_point || '-'}</span>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <span className="text-gray-500">{t.endPoint}</span>
              <span className="font-medium text-gray-900 text-right">{trail.end_point || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t.modality}</span>
              <span className="font-medium text-gray-900">{trail.modality || '-'}</span>
            </div>
            {trail.municipalities?.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t.municipalities}</span>
                <span className="font-medium text-gray-900 text-right">{trail.municipalities.join(', ')}</span>
              </div>
            )}
            {trail.natural_spaces?.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-gray-500">{t.naturalSpaces}</span>
                <span className="font-medium text-gray-900">{trail.natural_spaces.join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Technical Data */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#38B2AC]" />
            {t.technicalData}
          </h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 text-xs">{t.distance}</div>
              <div className="font-bold text-gray-900">{formatDistance(trail.distance_meters)}</div>
            </div>
            {trail.duration_hours && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-xs">{t.duration}</div>
                <div className="font-bold text-gray-900">{trail.duration_hours} {t.hours}</div>
              </div>
            )}
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-gray-500 text-xs">{t.elevationGain}</div>
              <div className="font-bold text-green-600">+{Math.round(trail.elevation_gain)} {t.m}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <div className="text-gray-500 text-xs">{t.elevationLoss}</div>
              <div className="font-bold text-red-600">-{Math.round(trail.elevation_loss)} {t.m}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 text-xs">{t.minAltitude}</div>
              <div className="font-bold text-gray-900">{Math.round(trail.min_altitude)} {t.m}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 text-xs">{t.maxAltitude}</div>
              <div className="font-bold text-gray-900">{Math.round(trail.max_altitude)} {t.m}</div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Lightbulb size={18} className="text-[#38B2AC]" />
            {t.recommendations}
          </h2>
          
          {/* Tips */}
          {trail.tips?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t.tips}</h3>
              <ul className="space-y-2">
                {(showAllTips ? trail.tips : trail.tips.slice(0, 2)).map((tip, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-[#38B2AC] mt-1">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
              {trail.tips.length > 2 && (
                <button
                  onClick={() => setShowAllTips(!showAllTips)}
                  className="text-sm text-[#38B2AC] mt-2 flex items-center gap-1"
                >
                  {showAllTips ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showAllTips 
                    ? (language === 'en' ? 'Show less' : language === 'de' ? 'Weniger' : 'Ver menos') 
                    : (language === 'en' ? `Show ${trail.tips.length - 2} more` : language === 'de' ? `${trail.tips.length - 2} mehr anzeigen` : `Ver ${trail.tips.length - 2} más`)
                  }
                </button>
              )}
            </div>
          )}

          {/* Best Season */}
          {trail.best_season && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar size={14} />
                {t.bestSeason}
              </h3>
              <p className="text-sm text-gray-600">{trail.best_season}</p>
            </div>
          )}

          {/* What to bring */}
          {trail.what_to_bring?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Backpack size={14} />
                {t.whatToBring}
              </h3>
              <div className="flex flex-wrap gap-2">
                {trail.what_to_bring.map((item, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleShare}
          >
            <Share2 size={16} className="mr-2" />
            {t.share}
          </Button>
          <Button
            asChild
            className="flex-1 bg-[#38B2AC] hover:bg-[#319795]"
          >
            <a href={trail.tenerifeon_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} className="mr-2" />
              {t.viewExternal}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrailDetailPage;
